import React from "react";
import s from "./CorridorMap.module.scss";

// Coordinates are in the pixel space of /images/hero_map.jpg (1536 x 884)
const W = 1536;
const H = 884;

// Real cities georeferenced onto the map (fitted from coastline control
// points); Caspian ports and Istanbul are snapped to the drawn coastline
const nodes = {
  urumqi: [1290, 415],
  almaty: [1157, 434],
  kyzylorda: [1010, 424],
  aktau: [852, 444],
  kashgar: [1145, 494],
  osh: [1105, 481],
  tashkent: [1061, 472],
  dushanbe: [1056, 515],
  ashgabat: [927, 534],
  turkmenbashi: [865, 497],
  baku: [812, 478],
  tbilisi: [762, 480],
  kars: [741, 500],
  ankara: [617, 516],
  istanbul: [558, 497],
  bucharest: [535, 434],
  chisinau: [569, 384],
  kyiv: [590, 315],
  lviv: [511, 327],
  vienna: [419, 356],
};

// Land legs of the corridor; the Caspian crossings are drawn separately
const routes = [
  ["urumqi", "almaty", "kyzylorda", "aktau"],
  ["urumqi", "kashgar", "osh", "tashkent", "ashgabat", "turkmenbashi"],
  [
    "baku",
    "tbilisi",
    "kars",
    "ankara",
    "istanbul",
    "bucharest",
    "chisinau",
    "vienna",
  ],
  ["kashgar", "dushanbe", "ashgabat"],
  ["chisinau", "kyiv", "lviv", "vienna"],
];
const crossings = [
  ["aktau", "baku"],
  ["turkmenbashi", "baku"],
];

const labels = [
  { at: "urumqi", text: "East", dx: 16, dy: 5, anchor: "start", big: true },
  { at: "almaty", text: "Kazakhstan", dx: -77, dy: -24 },
  { at: "osh", text: "Kyrgyzstan", dx: 30, dy: 31 },
  { at: "tashkent", text: "Uzbekistan", dx: -14, dy: -8, anchor: "end" },
  { at: "ashgabat", text: "Turkmenistan", dx: 0, dy: 24 },
  { at: "baku", text: "Azerbaijan", dx: 0, dy: 27 },
  { at: "tbilisi", text: "Georgia", dx: 0, dy: -16 },
  { at: "ankara", text: "Turkey", dx: 0, dy: 24 },
  { at: "chisinau", text: "Moldova", dx: 12, dy: 8, anchor: "start" },
  { at: "kyiv", text: "Ukraine", dx: 12, dy: 4, anchor: "start" },
  { at: "dushanbe", text: "Tajikistan", dx: 0, dy: 22 },
  { at: "vienna", text: "West", dx: 0, dy: -18, big: true },
];

// Freight in both directions: [route index, reverse?, duration s, delay s]
const cargo = [
  [0, false, 10, 0],
  [1, true, 11, 3],
  [2, false, 13, 1],
  [2, true, 13, 7.5],
  [4, true, 9, 4],
];

// Catmull-Rom spline through the points, as an SVG path
const smoothPath = (keys) => {
  const p = keys.map((k) => nodes[k]);
  let d = `M${p[0][0]},${p[0][1]}`;
  for (let i = 0; i < p.length - 1; i++) {
    const p0 = p[i - 1] || p[i];
    const p1 = p[i];
    const p2 = p[i + 1];
    const p3 = p[i + 2] || p2;
    const c1 = [p1[0] + (p2[0] - p0[0]) / 6, p1[1] + (p2[1] - p0[1]) / 6];
    const c2 = [p2[0] - (p3[0] - p1[0]) / 6, p2[1] - (p3[1] - p1[1]) / 6];
    d += ` C${c1[0]},${c1[1]} ${c2[0]},${c2[1]} ${p2[0]},${p2[1]}`;
  }
  return d;
};

const CorridorMap = () => {
  const paths = routes.map(smoothPath);

  return (
    <div className={s.map} aria-hidden="true">
      <img src="/images/hero_map.jpg" className={s.img} alt="" />
      <svg
        className={s.svg}
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="xMidYMin slice"
      >
        <defs>
          <filter id="routeGlow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="4" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {crossings.map((c) => (
          <path key={c.join()} d={smoothPath(c)} className={s.crossing} />
        ))}
        {routes.map((r, i) => (
          <path
            key={r.join()}
            d={paths[i]}
            className={s.route}
            style={{ animationDelay: `${i * 0.4}s` }}
            pathLength="1"
            filter="url(#routeGlow)"
          />
        ))}

        {/* freight moving along the corridor, westbound and eastbound */}
        {cargo.map(([route, reverse, dur, delay]) => (
          <circle
            key={`${route}-${reverse}`}
            r="5"
            className={reverse ? s.cargo_east : s.cargo}
          >
            <animateMotion
              dur={`${dur}s`}
              begin={`${delay}s`}
              repeatCount="indefinite"
              path={paths[route]}
              keyPoints={reverse ? "1;0" : "0;1"}
              keyTimes="0;1"
              calcMode="linear"
            />
          </circle>
        ))}

        {labels.map((l) => {
          const [x, y] = nodes[l.at];
          return (
            <g key={l.text}>
              <circle cx={x} cy={y} r="11" className={s.pulse} />
              <circle cx={x} cy={y} r={l.big ? 6 : 4.5} className={s.dot} />
              <text
                x={x + l.dx}
                y={y + l.dy}
                textAnchor={l.anchor || "middle"}
                className={l.big ? s.label_big : s.label}
              >
                {l.text}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

export default CorridorMap;
